//Library Imports
import { useRef, useEffect, Suspense, useState } from "react";
import { Radio } from "antd";
import { useSelector } from "react-redux";
import styled from "styled-components";

//Local Imports
import AutomationEmailSearch from "../Shared/AutomationEmailSearch";
import SentEmailSearch from "../../Campaigns/Emails/Components/SentEmailSearch";
import EmailItem from "./EmailItem";
import { IEmailData, ParamsObjectType, ControlNameEnum, IShareProfileTemplate, ISenderEmail } from "../interfaces";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import store, { RootState } from "store";
import { filtersList, getSendEmails } from "../utils";
import {
  emailsAPI,
  getEmailPreview,
  getSharedProfileData,
  resetWorkflowEmails,
  setSelectedEmailAction,
  updateFilterInStore,
  showUpdateButton,
} from "../actions";
import { AUTOMATION_EMAILS, AUTOMATION_USER_WORKFLOWS } from "constants/types/automationTypes";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import Message from "modules/Common/Components/Message";
import { ALERT_MESSAGES } from "constants/messages";
import ShareProfileTemplateItem from "./ShareProfileTemplateItem";
import useQuery from "../../../hooks/useQuery";
import AutomationSkeleton from "modules/Content/Shared/AutomationSkeleton";

interface IEmailList {
  isFormValid: () => any;
  enableSaveButton: (val: boolean) => void;
  selectedEmailId?: number;
  isFiltered?: boolean;
  changeHandler: (id: number) => void;
  isActionModal?: boolean;
  isWorkflow?: boolean;
  submitHandler?: (event: any) => void;
  isEditMode?: boolean;
  errorFields?: string[];
  setErrorFields?: any;
  loadingEmailUpdate?: boolean;
}

const EmailList: React.FC<IEmailList> = (props: IEmailList) => {
  // Props
  const {
    changeHandler,
    selectedEmailId,
    isActionModal,
    isWorkflow,
    submitHandler,
    errorFields,
    setErrorFields,
    isEditMode,
    loadingEmailUpdate,
    isFormValid,
    enableSaveButton,
  } = props;
  // hooks
  let query = useQuery();

  // Redux States
  const loading: boolean = useSelector((state: RootState) => state.automation.emails.loading);
  const emails: IEmailData[] = useSelector((state: RootState) => state.automation.emails.data);
  const emailsData: IEmailData[] = emails; // before the change I use getSendEmails function that will filter out the used emails in the workflow
  const totalCount: number = useSelector((state: RootState) => state.automation.emails.totalCount);
  const updateSuccess = useSelector((state: RootState) => state.automation.emails.updateSuccess);
  const emailFilters = useSelector((state: RootState) => state.automation.emails.filters);
  const searchTerm: string = useSelector((state: RootState) => state.automation.emails.searchTerm);
  const page: number = useSelector((state: RootState) => state.automation.emails.page);
  const shareProfileTemplates: IShareProfileTemplate[] = useSelector(
    (state: RootState) => state.automation.emails.shareProfileTemplates
  );
  const triggerId: number = useSelector((state: RootState) => state.automation.workflow.detail.triggerId);
  const senderEmails: ISenderEmail[] = useSelector((state: RootState) => state.automation.emails.senderEmails);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const actionModalFrom = useSelector((state: RootState) => state.automation.modals.addAction.from);
  const workflowSearchSuccess: boolean = useSelector((state: RootState) => state.automation.userWorkflows.success); // For Alpha case in search
  const workflowSearchErrorMessage: string = useSelector(
    (state: RootState) => state.automation.userWorkflows.message
  ); // For Alpha case in search
  const selectedEmailIdRedux = useSelector((state: RootState) => state.automation.emails.selectedEmail);
  const selectedEmailIdReduxId = selectedEmailIdRedux?.id;

  const [selectedEmailNew, setSelectedEmailNew] = useState(selectedEmailId);

  // sender name from user profile
  const { first_name, last_name } = useSelector((state: RootState) => state.user.userData);
  const fullName = first_name + " " + last_name;
  // reference
  const emailsListRef = useRef(null);

  // Local Variables
  const getPagination = (reset?: boolean, searchValue?: string, emailId?: number) => {
    let params: ParamsObjectType = {
      limit: 10,
      offset: reset ? 0 : emails.length,
    };
    if (emailFilters && !isWorkflow)
      params.email_type = emailFilters === "digest" ? "0" : emailFilters === "adhoc" ? "1" : "2";
    // if (isActionModal) params.ready_to_publish = true;
    if (searchValue) {
      params.search_term = searchValue;
    } else if (searchTerm) {
      if (searchValue !== "") params.search_term = searchTerm;
    }
    if (isWorkflow) {
      params.workflow_id = query.get("workflow_id");
    }
    if ((isEditMode || actionModalFrom === "trigger") && (selectedEmailNew || emailId)) {
      params.workflow_email_id = emailId ? emailId : selectedEmailNew;
      // params.workflow_email_id = 93011; //* testing using Sameed's Email
    }
    return params;
  };

  // Component Logic
  const updateSearchTermInStore = (value: string) => {
    store.dispatch({
      type: AUTOMATION_EMAILS.SET_SEARCH_TERM,
      payload: value,
    });
  };

  const searchHandler = (value: string) => {
    emailsListRef?.current?.scroll({
      top: 0,
      behavior: "smooth",
    });
    emailsAPI(getPagination(true, value));
    updateSearchTermInStore(value);
    enableSaveButton(false);
  };

  const resetSearchHandler = () => {
    updateSearchTermInStore("");
    if (emailFilters.length > 0 && !loading) emailsAPI(getPagination(true, ""));
    store.dispatch({
      type: AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM,
      payload: { message: "", success: true },
    });
  };

  useEffect(() => {
    getSharedProfileData();
    return () => {
      updateSearchTermInStore("");
      updateFilterInStore("allTypes");
      resetWorkflowEmails();
    };
  }, []);

  useEffect(() => {
    if (filtersList.includes(emailFilters) && !loading) {
      emailsAPI(getPagination(true));
    }
  }, [emailFilters]);

  const handleNext = () => {
    if (!loading) emailsAPI(getPagination());
  };

  useEffect(() => {
    if (!actionModalVisible) {
      setSelectedEmailNew(0);
      setSelectedEmailAction(0, "", 0);
      resetSearchHandler();
    } else {
      // Every time the modal opens
      resetWorkflowEmails();
      emailsAPI(getPagination(isActionModal));
    }
  }, [actionModalVisible]);

  useEffect(() => {
    if (selectedEmailNew === 0 && selectedEmailIdReduxId !== 0 && isEditMode) {
      setSelectedEmailNew(selectedEmailIdReduxId);
      resetWorkflowEmails();
      emailsAPI(getPagination(true, "", selectedEmailIdReduxId));
    }
  }, [selectedEmailIdReduxId, selectedEmailNew, actionModalFrom, isEditMode]);

  const onShowPreview = (id: number, campaignId: number) => {
    getEmailPreview(id, campaignId);
  };

  const buildUI = () => {
    return (
      <InfiniteLoader
        dataLength={
          triggerId === 10 && shareProfileTemplates.length > 0
            ? emails.length + shareProfileTemplates.length
            : emails.length
        }
        hasMore={true}
        next={handleNext}
        scrollableTarget="automation-emails"
        loader={loading && page > 0 ? <AutomationSkeleton /> : <></>}
      >
        <Radio.Group
          onChange={(event) => {
            changeHandler(event.target.value);
          }}
          value={selectedEmailId}
        >
          {page === 0 && loading ? (
            <AutomationSkeleton />
          ) : (
            <div>
              {triggerId === 10 && <ShareProfileTemplateItem id={0.5} title={"Share Profile"} />}
              {emailsData.length > 0 && workflowSearchSuccess ? (
                emailsData.map((email: IEmailData) => (
                  <EmailItem
                    key={email.emailId}
                    id={email.emailId}
                    versionEmailType={email.versionEmailType}
                    designReady={email.designReady}
                    campaignId={email.campaignId}
                    isActionModal={isActionModal}
                    emailName={email.title}
                    subject={email.subject}
                    sendFrom={email.sendFrom}
                    senderName={email.senderName ?? fullName}
                    date={email.date}
                    isFormValid={isFormValid}
                    thumbnail={email.thumbnail}
                    campaignName={email.campaignName}
                    senderEmails={senderEmails}
                    setErrorFields={setErrorFields}
                    selectedEmailId={selectedEmailId}
                    onShowPreview={onShowPreview}
                    loadingEmailUpdate={loadingEmailUpdate}
                    enableSaveButton={enableSaveButton}
                  />
                ))
              ) : !loading ? (
                <EmptyTabInfo
                  description={workflowSearchSuccess ? "No Emails available." : workflowSearchErrorMessage}
                />
              ) : null}
            </div>
          )}
        </Radio.Group>
      </InfiniteLoader>
    );
  };

  const SearchComponent = isWorkflow ? AutomationEmailSearch : SentEmailSearch;

  return (
    <Wrapper isActionModal={isActionModal} className={isActionModal ? "w-100" : ""}>
      <div className="wrapper-label d-flex justify-content-between">
        <div>
          <h3>Emails</h3>
          <span hidden={isActionModal}>Draft emails will be showing</span>
        </div>
        <div className={`search-wrapper ${isActionModal ? "action-modal" : ""}`}>
          <SearchComponent
            handleSearch={searchHandler}
            reset={resetSearchHandler}
            placeholder="Search emails / Campaigns"
          />
        </div>
      </div>
      {isActionModal && (
        <Suspense fallback={<></>}>
          {errorFields && errorFields?.length > 0 ? (
            <div className="message-div">
              <Message
                message={ALERT_MESSAGES.WORKFLOW_EMAILS_ACTION_ERROR}
                label={""}
                // type="info"
                marginTop="14"
                cross={false}
                subMessage={`"${errorFields.join('" "')}"`}
                type={"errorRed"}
                smPadding
              />
            </div>
          ) : updateSuccess ? (
            <div className="message-div">
              <Message
                message={ALERT_MESSAGES.WORKFLOW_EMAILS_ACTION_UPDATE_SUCCESSFUL}
                label="Heads up!"
                marginTop="14"
                cross={false}
                smPadding
              />
            </div>
          ) : (
            <div className="message-div">
              <Message
                message={ALERT_MESSAGES.WORKFLOW_EMAILS_ACTION}
                label="Heads up!"
                type="info"
                marginTop="14"
                cross={false}
                smPadding
              />
            </div>
          )}
        </Suspense>
      )}
      {isWorkflow ? (
        <form id={`${ControlNameEnum.SEND_EMAIL}`} onSubmit={submitHandler}>
          <div
            ref={emailsListRef}
            id="automation-emails"
            className={`scrollable-component ${isActionModal ? "action-modal" : ""}`}
          >
            {buildUI()}
          </div>
        </form>
      ) : (
        <div
          ref={emailsListRef}
          id="automation-emails"
          className={`scrollable-component ${isActionModal ? "action-modal" : ""}`}
        >
          {buildUI()}
        </div>
      )}
    </Wrapper>
  );
};

const defaultProps = {
  isActionModal: false,
};

EmailList.defaultProps = defaultProps;

export default EmailList;

const Wrapper = styled.div<{ isActionModal?: boolean }>`
  width: calc(100% - 265px);
  max-height: calc(100vh - 290px);
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    width: calc(100% - 200px);
  }
  .message-div {
    padding: 0 10px 10px;
    margin-top: -5px;
    max-height: 65px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: 62px;
    }
  }
  .scrollable-component {
    /* height: 200px; */
    max-height: calc(100vh - 400px);
    /* height: calc(100vh - 400px); */
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-height: calc(100vh - 300px);
      /* height: calc(100vh - 300px); */
      height: 100vh;
    }
    &.action-modal {
      max-height: 412px;
      /* height: calc(100vh - 690px); */
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        max-height: 331px;
        /* max-height: calc(100vh - 470px);
        height: calc(100vh - 470px); */
      }
    }
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 15px;
      margin-bottom: 15px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #01218a;
    }
    .ant-radio-group {
      width: 100%;
      padding-top: 10px;
    }
  }

  .wrapper-label {
    /* padding: 16px 23px 16px 16px; */
    width: 100%;
    position: sticky;
    top: 0;
    background: white;
    box-shadow: 0 2px 8px #e3e3e3;
    z-index: 999;
    padding: 8px 23px 8px 16px;
    align-items: center;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding: 12px 23px 12px 16px;
    }
    .search-wrapper {
      width: 250px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 240px;
      }
      .search-wraper .search-bar .search-input {
        margin-top: 3px;
      }
      &.action-modal {
        width: 250px !important;
      }import { useState } from 'react';
import { useState } from 'react';

    }
    h3 {
      color: #424b50;
      margin: ${(props) => (props.isActionModal ? "0" : "0 0 5px 0")};
      font-weight: 500;
      font-size: ${(props) => (props.isActionModal ? "18px" : "16px")};
      font-family: "Roboto", sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: ${(props) => (props.isActionModal ? "15px" : "13px")};
      }
    }
    span {
      font-family: "Roboto", sans-serif;
      color: #949696;
      font-size: 14px;
      font-weight: 500;
      display: block;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
    }
  }
`;
